import React, {useContext, useState, useMemo, useEffect, useCallback} from 'react';
import { Route, useLocation } from 'react-router';
import SearchStore from 'js/store/SearchStore';
import SeoHelmet from 'js/containers/SEO/SeoHelmet';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';
import { CommonContext } from 'js/context/CommonContext/CommonContext';
import SingleVideos from 'js/containers/PostTypes/Videos/SingleVideos';
import SingleArticles from 'js/containers/PostTypes/Articles/SingleArticles';
import SinglePodcasts from 'js/containers/PostTypes/Podcasts/SinglePodcasts';
import SelfLearning from 'js/containers/PostTypes/SelfLearning/SelfLearning';
import ResourceVisibilityValidator from 'js/components/Common/ResourceVisibilityValidator/ResourceVisibilityValidator';

import fetchData from './fetchData';
import { SinglePostTypeRouterDataInterface } from './SinglePostRouterDataInterface';

type TypesType = 'articles' | 'videos' | 'podcasts' | 'self-learning';
type EntityTypesType = 'article' | 'video' | 'podcast' | 'self-learning';

const useSinglePostRouter = () => {
    const location = useLocation();
    const types:Array<TypesType> = useMemo(() => ['articles', 'videos', 'podcasts', 'self-learning'], []);
    const {isCustomerLogged, apiPath} = useContext(CommonContext);
    const [currentType, setCurrentType] = useState<TypesType>(null);
    const [currentUri, setCurrentUri] = useState<string>('');
    const [post, setPost] = useState<SinglePostTypeRouterDataInterface>(null);

    useEffect(() => {
        if (!types.length) {
            setPost(null);
            setCurrentType(null);
            setCurrentUri('');
            return;
        }

        const path: string = location.pathname;
        for (let i = 0; i < types.length; i++ ) {
            const type = types[i];
            const regexp: RegExp = new RegExp(`^/${type}/`);
            if (regexp.test(path)) {
                const uri = path.replace(regexp, '').replace('/', '');
                setCurrentType(type);
                setCurrentUri(uri);
            }
        }
    }, [location, types]);

    useEffect(() => {
        if (!currentType) {
            return;
        }
        let isCanceled = false;
        const query = currentType !== 'self-learning' ? `${currentType}Single` : 'post';
        (async () => {
            const response = await fetch(`${apiPath}/graphql`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: `${fetchData(query, currentUri, currentType)}`
                })
            });
            if (isCanceled) { return; }
            const { data } = await response.json();
            setPost(data[query]);
        })();
        return () => {
            isCanceled = true;
        };
    }, [apiPath, currentType, currentUri]);

    const getComponentByType = useCallback(() => {
        switch (currentType) {
            case 'articles' : return <SingleArticles pageId={post?.databaseId}/>;
            case 'podcasts' : return <SinglePodcasts pageId={post?.databaseId}/>;
            case 'videos' : return <SingleVideos pageId={post?.databaseId}/>;
            case 'self-learning' : return <SelfLearning pageId={post?.databaseId} />;
            default : return null;
        }
    }, [currentType, post]);

    const {INIT_MIX_PANEL} = useMemo(() => MixPanelWrapper, []);

    useEffect(() => {
        if (!post) {
            return;
        }
        const data: {page: string, product?: string, type?: string, entityType?: 'article' | 'video' | 'podcast' | 'self-learning' } = {
            page: post?.title
        };
        let typeEntityVar:EntityTypesType = 'self-learning';
        if (currentType !== 'self-learning') {
            let typeQueryVar = '';
            switch (currentType) {
                case 'articles' :
                    typeQueryVar = 'articleTypeList';
                    typeEntityVar = 'article';
                break;
                case 'videos' :
                    typeQueryVar = 'videoTypeList';
                    typeEntityVar = 'video';
                    break;
                case 'podcasts' :
                    typeQueryVar = 'podcastTypeList';
                    typeEntityVar = 'podcast';
                    break;
                default : typeQueryVar = '';
            }
            data.type = post[typeQueryVar]?.nodes?.map(item => item?.name).join(',');
            data.product = post?.productLegacyList?.nodes?.map(item => item?.name).join(',');
        }
        data.entityType = typeEntityVar;
            (async () => {
            await MixPanelWrapper.TrackPageView(data);
        })();
    }, [post, INIT_MIX_PANEL, currentType]);

    useEffect(() => {
        if (!post) {
            return;
        }
        SearchStore.setSourcePage({
            name: post?.title,
            uri: post?.uri,
            parent: null
        });
    }, [post]);

    return (
        !!post?.databaseId && isCustomerLogged &&
        <ResourceVisibilityValidator redirect={true} postID={post?.databaseId}>
            <Route
                    exact
                    key={post?.databaseId}
                    path={`/${currentType}/:slug`}
                    render={
                () => (
                    <>
                        <SeoHelmet
                            title={post?.SeoMetadata?.metaTitle || post?.title}
                            description={ post?.SeoMetadata?.metaDescription ?? ''}
                            canonical={post?.link} />
                        {getComponentByType()}
                    </>
                )
            }/>
	    </ResourceVisibilityValidator>
    );
};

export default useSinglePostRouter;
